/**
 * 邮箱模块API
 * 处理发件箱、收件箱等邮件列表相关操作
 */

import apiClient from "./axios";

/**
 * 获取发件箱邮件列表（当前用户发送的邮件）
 * @param {Object} params - 查询参数
 * @param {number} params.page - 页码
 * @param {number} params.page_size - 每页数量
 * @param {string} [params.subject] - 主题关键词
 * @param {string} [params.to_address] - 收件人邮箱
 * @param {string} [params.status] - 邮件状态
 * @param {string} [params.start_date] - 开始日期
 * @param {string} [params.end_date] - 结束日期
 * @returns {Promise} API响应
 */
export const getOutboxList = async (params = {}) => {
    try {
        const response = await apiClient.get("/v1/mail/outbox", { params });
        return response;
    } catch (error) {
        console.error("获取发件箱列表失败:", error);
        throw error;
    }
};

/**
 * 获取邮件详情
 * @param {number} emailId - 邮件ID
 * @returns {Promise} API响应
 */
export const getEmailDetail = async (emailId) => {
    try {
        const response = await apiClient.get(`/v1/mail/emails/${emailId}/content`);
        return response;
    } catch (error) {
        console.error("获取邮件详情失败:", error);
        throw error;
    }
};

/**
 * 获取发件箱邮件的"重发草稿种子"——用于预填编辑器主题/正文/附件/业务上下文
 *
 * 设计文档：docs/fzx_doc/邮件/发件箱邮件重发/发件箱邮件重发_功能规格说明书.md
 * 后端契约：GET /v1/mail/emails/{id}/resend-draft（响应结构见 backend/schemas/mail/resend_draft.py）
 *
 * 调用方：
 * - GenericEmailSendDrawer（T06）：拿到 resendSourceEmailId prop 时调用本接口加载种子
 * - OutboxList / EmailDetailDrawer：列表/详情页本身不直接调用，按钮显隐/置灰用 outbox 列表已带的 template_available 字段判断
 *
 * 错误语义：
 * - 404 邮件不存在 / 非 OUTBOUND
 * - 400 邮件状态为 PendingSend（FR-006）
 * - 500 服务内部错误
 *
 * @param {number} emailId - 原邮件 ID
 * @returns {Promise<Object>} ResendDraft 结构（含 source_*、subject、body_html、attachments[]、content_block_context、business_link 等）
 */
export const getEmailResendDraft = async (emailId) => {
    try {
        const response = await apiClient.get(`/v1/mail/emails/${emailId}/resend-draft`);
        return response;
    } catch (error) {
        console.error("获取邮件重发草稿种子失败:", error);
        throw error;
    }
};

/**
 * 终止群发邮件发送
 * @param {number} jobId - 邮件任务ID
 * @returns {Promise} API响应
 */
export const cancelMailJob = async (jobId) => {
    try {
        const response = await apiClient.post(`/v1/mail/outbox/${jobId}/cancel`);
        return response;
    } catch (error) {
        console.error("终止邮件任务失败:", error);
        throw error;
    }
};

/**
 * 补发群发邮件中未完成的收件人
 * @param {number} jobId - 邮件任务ID
 * @returns {Promise} API响应
 */
export const resumeUnfinishedMailJob = async (jobId) => {
    try {
        const response = await apiClient.post(`/v1/mail/outbox/${jobId}/resume-unfinished`);
        return response;
    } catch (error) {
        console.error("补发未完成收件人失败:", error);
        throw error;
    }
};

/**
 * 获取自动转发监控列表（含跳过 / 失败决策）
 * @param {Object} params - 查询参数
 * @param {number} params.page - 页码
 * @param {number} params.page_size - 每页数量
 * @param {string} [params.rule_category_id] - 规则分类标识，如 forward_zto_mvt
 * @param {string} [params.decision] - sent / skipped_duplicate / failed_no_recipients / failed_other
 * @param {string} [params.subject_keyword] - 原邮件主题关键词
 * @param {string} [params.start_date] - 原邮件起始日期 YYYY-MM-DD
 * @param {string} [params.end_date] - 原邮件结束日期 YYYY-MM-DD
 * @returns {Promise} API响应
 */
export const getAutoForwardList = async (params = {}) => {
    try {
        const response = await apiClient.get("/v1/mail/auto-forward/logs", { params });
        return response;
    } catch (error) {
        console.error("获取自动转发监控列表失败:", error);
        throw error;
    }
};

export default {
    getOutboxList,
    getEmailDetail,
    getEmailResendDraft,
    cancelMailJob,
    resumeUnfinishedMailJob,
    getAutoForwardList,
};
